import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProjectHeader from '@/components/header';
import { Card, CardContent, CardTitle, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { ProjectService } from '@/services/ProjectService';
import { FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { ReactComponent as Other } from '@/components/icons/other-new.svg';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import ApplianceOptionsDialog from '../components/appliancesComponents/ApplianceOptionsDialog';
import CollectionNavigation from '@/components/CollectionNavigation';
import ApplianceDeleteDialog from '../components/appliancesComponents/ApplianceDeleteDialog';
import { useCollectionData } from '@/hooks/useCollectionData';
import { AssetsConfig } from '@/services/AssetsConfig';


const API_BASE_URL =
    process.env.REACT_SEARCH_APP_API_BASE_URL || 'https://xppqnn7pn6.execute-api.us-east-1.amazonaws.com/default';
const api = axios.create({
    baseURL: API_BASE_URL,
});

const CabinetSelector = ({ flowId }) => {
    const [selectedCabinets, setSelectedCabinets] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentCabinet, setCurrentCabinet] = useState(null);
    const [sizeType, setSizeType] = useState('');
    const [customSize, setCustomSize] = useState({ width: '', depth: '', height: '', additionalInfo: '' });
    const [searchProduct, setSearchProduct] = useState('');
    const [customName, setCustomName] = useState('');
    const [standardSize, setStandardSize] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const { projectId } = useParams();
    const [allCabinets, setAllCabinets] = useState({});
    const [newCabinetsOther, setNewCabinetsOther] = useState(false);
    const [otherParametrs, setOtherParametrs] = useState(false);
    const [isOptionsDialogOpen, setIsOptionsDialogOpen] = useState(false);
    const [selectedCabinetForOptions, setSelectedCabinetForOptions] = useState(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [cabinetType, setCabinetType] = useState('');
    const [projectData, setProjectData] = useState(null);
    const [isPanelReady, setIsPanelReady] = useState(false);
    const { data: collectionData, iscollectionLoading } = useCollectionData(projectData?.collectionId);
    const [cabinetConfig, setCabinetConfig] = useState({
        cabinets: [],
        standardSizes: {},
        cabinetTypes: {}
    });

    useEffect(() => {
        async function fetchCabinets() {
            setIsLoading(true);
            try {
                const currentProject = await ProjectService.getProjectById(projectId);
                setProjectData(currentProject);

                // Load cabinet configuration from S3
                const config = await AssetsConfig.getCabinets(null, currentProject?.type || 'kitchen');
                setCabinetConfig(config);
                setAllCabinets(config.cabinets);

                if (currentProject.cabinets) {
                    let savedCabinets = currentProject.cabinets;

                    if (typeof currentProject.cabinets === 'string') {
                        try {
                            savedCabinets = JSON.parse(currentProject.cabinets);
                        } catch (parseError) {
                            console.error('Error parsing cabinets string:', parseError);
                        }
                    }

                    // Map the saved cabinets to include the icon URL
                    const cabinetsWithIcons = savedCabinets.map(cabinet => {
                        const baseId = cabinet.id.split('-')[0];
                        const cabinetTemplate = config.cabinets.find(c => c.id === baseId);
                        
                        return {
                            ...cabinet,
                            iconUrl: cabinetTemplate?.iconUrl || config.cabinets.find(c => c.id === 'other')?.iconUrl
                        };
                    });

                    setSelectedCabinets(cabinetsWithIcons);
                }
            } catch (error) {
                console.error('Error in fetchCabinets:', error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchCabinets();
    }, [projectId]);

    useEffect(() => {
        const DELAY_TIME = 5000;
        let timeoutId;

        const resetTimeout = () => {
            clearTimeout(timeoutId); // Clear any existing timeout
            timeoutId = setTimeout(() => {
                saveCabinets(); // Save cabinets if no click for 5 seconds
            }, DELAY_TIME);
        };

        if (selectedCabinets.length > 0) {
            document.addEventListener('click', resetTimeout);

            // Call resetTimeout initially to set the timeout
            resetTimeout();

            // Cleanup event listener and timeout on unmount
            return () => {
                document.removeEventListener('click', resetTimeout);
                clearTimeout(timeoutId);
            };
        }
    }, [selectedCabinets, allCabinets]);

    const saveCabinets = async () => {
        try {
            const cabinetsToSave = selectedCabinets.map(cabinet => ({
                id: cabinet.id,
                name: cabinet.name,
                sizeInfo: cabinet.sizeInfo,
                dimensions: {
                    width: cabinet.dimensions.width.toString(),
                    depth: cabinet.dimensions.depth.toString(),
                    height: cabinet.dimensions.height.toString(),
                },
                additionalInfo: cabinet.additionalInfo,
                sizeType: cabinet.sizeType,
                productId: cabinet.productId,
                panel_Ready: cabinet.panelReady
            }));

            const response = await ProjectService.updateProject(projectId, {
                cabinets: cabinetsToSave,
            });
        } catch (error) {
            console.error('Failed to save cabinets:', error);
        }
    };

    useEffect(() => {
        if (sizeType === 'standard' && !otherParametrs && currentCabinet?.id) {
            // const sizes = applianceConfig.standardSizes[currentAppliance.id];
            const sizes = cabinetConfig.cabinets.find(app => app.id === currentCabinet.id)?.standardSizes || [];
            if (sizes && sizes.length > 0) {
                setStandardSize(JSON.stringify(sizes[0]));
            }
        }
    }, [sizeType, otherParametrs, currentCabinet, cabinetConfig]);

    const handleComplete = () => {
        saveCabinets();
        async function fetchCompletedFlow() {
            await ProjectService.addCompletedFlow(projectId, flowId);
            navigate(`/project/${projectId}`);
        }
        fetchCompletedFlow();
    };

    const handleChangeSize = e => {
        setStandardSize(e.target.value);
    };

    const handleCabinetClick = cabinet => {
        const existingCabinet = selectedCabinets.find(
            a => a.id === cabinet.id || a.id.startsWith(`${cabinet.id}-`)
        );

        if (existingCabinet) {
            setSelectedCabinetForOptions(existingCabinet);
            setIsOptionsDialogOpen(true);
        } else {
            openDialog(cabinet);
        }
    };

    const openDialog = cabinet => {
        setCurrentCabinet(cabinet);

        if (cabinet.id === 'other') {
            setCurrentCabinet(null);
            setOtherParametrs(true);
            setNewCabinetsOther(true);
            setSizeType('');
            setIsDialogOpen(true);
        } else {
            setNewCabinetsOther(false);
            const existingCabinet = selectedCabinets.find(a => a.id === cabinet.id);
            setOtherParametrs(false);
            if (existingCabinet) {
                setSizeType(existingCabinet.sizeType || '');
                setCabinetType(existingCabinet.cabinetType || '');
                setIsPanelReady(existingCabinet.panelReady || false);
                if (existingCabinet.dimensions) {
                    const dimensionStr = JSON.stringify(existingCabinet.dimensions);
                    setStandardSize(dimensionStr);
                    setCustomSize({
                        width: existingCabinet.dimensions.width.toString(),
                        depth: existingCabinet.dimensions.depth.toString(),
                        height: existingCabinet.dimensions.height.toString(),
                        additionalInfo: existingCabinet.additionalInfo || '',
                    });
                }
            } else {
                setSizeType('standard');
                setCustomSize({ width: '', depth: '', height: '', additionalInfo: '' });
                setCabinetType('');
                setIsPanelReady(false);
            }
            setSearchProduct('');
            setSearchResults([]);
            setIsDialogOpen(true);
        }
    };

    const handleDeleteFromOptions = () => {
        if (selectedCabinetForOptions) {
            const baseId = selectedCabinetForOptions.id.split('-')[0];
            const categoryCabinets = selectedCabinets.filter(a => a.id === baseId || a.id.startsWith(`${baseId}-`));

            if (categoryCabinets.length > 1) {
                setCategoryToDelete({
                    name: selectedCabinetForOptions.name,
                    baseId: baseId,
                    cabinets: categoryCabinets,
                });
                setIsDeleteDialogOpen(true);
                setIsOptionsDialogOpen(false);
            } else {
                handleRemoveCabinet(selectedCabinetForOptions.id);
                setIsOptionsDialogOpen(false);
            }
            setSelectedCabinetForOptions(null);
        }
    };

    const handleAddAnotherFromOptions = () => {
        if (selectedCabinetForOptions) {
            const baseId = selectedCabinetForOptions.id.split('-')[0];
            const cabinetTemplate = allCabinets.find(a => a.id === baseId);

            const isCustomCabinet = !cabinetConfig.cabinets.find(a => a.id === baseId);

            if (isCustomCabinet) {
                setOtherParametrs(true);
            }

            openDialog(cabinetTemplate);
            setIsOptionsDialogOpen(false);
            setSelectedCabinetForOptions(null);
        }
    };

    const generateUniqueId = baseId => {
        const existingCount = selectedCabinets.filter(a => a.id.startsWith(baseId)).length;
        return `${baseId}-${existingCount + 1}`;
    };

    const handleOtherCabinetSave = () => {
        const newCabinet = {
            id: 'other_' + customName.toLowerCase().replace(/\s+/g, '-'),
            name: customName,
            icon: Other,
        };

        setCurrentCabinet(newCabinet);
        setNewCabinetsOther(false);
        setCustomName('');
    };

    const handleSaveCabinet = (result = null) => {
        setOtherParametrs(false);
        let dimensions;
        let cabinet;

        const uniqueId =
            currentCabinet.id === selectedCabinetForOptions?.id
                ? selectedCabinetForOptions.id
                : generateUniqueId(currentCabinet.id);

        // Create the merged name for sink and microwave
        let mergedName = currentCabinet.name;
        if ((currentCabinet.id === 'sink' || currentCabinet.id === 'microwave') && cabinetType) {
            if (currentCabinet.id === 'sink') {
                mergedName = `${cabinetType} Sink`;
            } else if (currentCabinet.id === 'microwave') {
                mergedName = `${cabinetType} Microwave`;
            }
        }

        if (result && sizeType === 'product') {
            dimensions = {
                width: parseFloat(result.W),
                depth: parseFloat(result.D),
                height: parseFloat(result.H),
            };
            cabinet = {
                ...currentCabinet,
                id: uniqueId,
                name: mergedName,
                dimensions,
                productId: result['product #'],
                sizeType: undefined,
                cabinetType: cabinetType,
                panelReady: isPanelReady,
            };
        } else {
            if (sizeType === 'standard') {
                dimensions = JSON.parse(standardSize);
            } else if (sizeType === 'custom') {
                dimensions = {
                    width: parseFloat(customSize.width),
                    depth: parseFloat(customSize.depth),
                    height: parseFloat(customSize.height),
                };
            }
            cabinet = {
                ...currentCabinet,
                id: uniqueId,
                name: mergedName,
                dimensions,
                additionalInfo: customSize.additionalInfo,
                sizeType,
                cabinetType: cabinetType,
                panelReady: isPanelReady, // Add panelReady property
            };
        }

        // Update selectedCabinets
        setSelectedCabinets(prev => {
            if (selectedCabinetForOptions) {
                return prev.map(a => (a.id === selectedCabinetForOptions.id ? cabinet : a));
            } else {
                return [...prev, cabinet];
            }
        });

        closeDialog();
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setCurrentCabinet(null);
        setSizeType('');
        setCustomSize({ width: '', depth: '', height: '', additionalInfo: '' });
        setSearchProduct('');
        setStandardSize('');
        setOtherParametrs(false);
        setNewCabinetsOther(false);
        setSearchResults([]);
        setCabinetType('');
        setIsPanelReady(false);
    };

    const isCustomSizeValid = () => {
        return customSize.width !== '' && customSize.depth !== '' && customSize.height !== '';
    };

    const isStandardSizeValid = () => {
        return standardSize !== '';
    };

    const handleSearchProduct = async () => {
        if (searchProduct) {
            setIsSearching(true);
            const newSearch = {
                cabinet_type: currentCabinet.name.toLowerCase(),
                prompt: searchProduct,
            };
            try {
                const response = await api.post('/bedrock_invoke_lambda', newSearch);

                const data = response.data;

                if (data.answer && Array.isArray(data.answer)) {
                    setSearchResults(data.answer);
                }
            } catch (error) {
                console.error('Search failed:', error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }
    };
    const handleSizeTypeChange = value => {
        setSizeType(value);
    };

    

    const renderPanelReadyCheckbox = () => {
        if (!currentCabinet) return null;
    
        // Find the cabinet template in the config
        const cabinetTemplate = cabinetConfig.cabinets.find(a => a.id === currentCabinet.id);
        
        // Show checkbox if it's either:
        // 1. A custom cabinet (other_)
        // 2. A cabinet that can be panel ready according to the config
        if (!currentCabinet.id?.startsWith('other_') && 
            (!cabinetTemplate || !cabinetTemplate.canBePanelReady)) {
            return null;
        }
    
        return (
            <div className="flex items-center space-x-2 mt-4">
                <Checkbox
                    id="panelReady"
                    checked={isPanelReady}
                    onCheckedChange={checked => setIsPanelReady(checked)}
                />
                <Label htmlFor="panelReady">Panel Ready</Label>
            </div>
        );
    };

    const handleSelectSearchResult = result => {
        handleSaveCabinet(result);
    };

    const handleRemoveCabinet = cabinetId => {
        const baseId = cabinetId.split('-')[0];
        const isCustomCabinet = !cabinetConfig.cabinets.find(a => a.id === baseId);

        setSelectedCabinets(prev => {
            const newSelectedCabinets = prev.filter(a => a.id !== cabinetId);
            if (isCustomCabinet) {
                const remainingCount = newSelectedCabinets.filter(a => a.id.split('-')[0] === baseId).length;

                if (remainingCount === 0) {
                    setAllCabinets(currentAll => currentAll.filter(a => a.id !== baseId));
                }
            }
            const cabinetsToSave = newSelectedCabinets.map(cabinet => ({
                id: cabinet.id,
                name: cabinet.name,
                sizeInfo: cabinet.sizeInfo,
                dimensions: cabinet.dimensions && {
                    width: cabinet.dimensions.width.toString(),
                    depth: cabinet.dimensions.depth.toString(),
                    height: cabinet.dimensions.height.toString(),
                },
                additionalInfo: cabinet.additionalInfo,
                sizeType: cabinet.sizeType,
                productId: cabinet.productId,
                panel_Ready: cabinet.panelReady,
            }));

            ProjectService.updateProject(projectId, {
                cabinets: cabinetsToSave,
            }).catch(error => {
                console.error('Failed to save cabinets:', error);
            });

            return newSelectedCabinets;
        });
    };

    const renderSearchResults = () => {
        if (!searchResults.length) return null;

        return (
            <div className="mt-4">
                <h3 className="font-semibold mb-2">Search Results</h3>
                <div className="max-h-60 overflow-y-auto">
                    {searchResults.map((result, index) => (
                        <div key={index} className="flex items-center justify-between p-2 border-b hover:bg-gray-50">
                            <div className="flex-1">
                                <p className="font-medium">
                                    {result.brand} - {result['product #']}
                                </p>
                                <p className="text-sm text-gray-600">
                                    W: {result.W}" × D: {result.D}" × H: {result.H}"
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(result.search_link, '_blank')}
                                >
                                    <FaExternalLinkAlt className="w-4 h-4" />
                                </Button>
                                <Button size="sm" onClick={() => handleSelectSearchResult(result)} className="px-6">
                                    Select
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderProductSearch = () => (
        <div className="space-y-4">
            <div>
                <Label htmlFor="searchProduct">Search {currentCabinet?.name || 'Product'}</Label>
                <div className="flex mt-1">
                    <Input
                        id="searchProduct"
                        value={searchProduct}
                        onChange={e => setSearchProduct(e.target.value)}
                        className="flex-grow"
                        placeholder="Enter Model, Brand or any description"
                    />
                    <Button onClick={handleSearchProduct} className="ml-2" disabled={isSearching}>
                        <FaSearch className="mr-2" />
                        {isSearching ? 'Searching...' : 'Search'}
                    </Button>
                </div>
            </div>
            {renderSearchResults()}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-100 p-8">
            <ProjectHeader showBackButton={true} collectionData={collectionData} projectName={projectData?.name} />
            <main className="pt-20 px-4 text-center">
                {/* Render navigation only if project is part of a collection */}
                {projectData?.collectionId && (
                    <CollectionNavigation
                        // collectionId={projectData.collectionId}
                        collectionData={collectionData}
                        isLoading={iscollectionLoading}
                        currentProjectId={projectId}
                    />
                )}
                <h1 className="text-3xl font-bold mb-8">Select the cabinets to incorporate in your design</h1>
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                ) : (
                    <>
                        <Card className="max-w-6xl mx-auto mb-8">   
                            <CardContent className="pt-4 px-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                                {allCabinets.map(cabinet => {
                                    const count = selectedCabinets.filter(
                                        a => a.id === cabinet.id || a.id.startsWith(`${cabinet.id}-`)
                                    ).length;
                                    const isSelected = count > 0;

                                    return (
                                        <button
                                            key={cabinet.id}
                                            onClick={() => handleCabinetClick(cabinet)}
                                            className={`p-4 rounded-lg flex flex-col items-center justify-center transition-all hover:scale-105 relative ${
                                                isSelected
                                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                            }`}
                                        >
                                            <img
                                                src={cabinet.iconUrl}
                                                alt={cabinet.name}
                                                className="w-8 h-8"
                                            />
                                            <span className="mt-2 text-sm">{cabinet.name}</span>
                                            {count > 0 && (
                                                <span className="absolute top-1 right-1 bg-white text-green-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-medium">
                                                    {count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </CardContent>
                        </Card>
                        <Card className="max-w-6xl mx-auto mb-8">
                            <CardHeader>
                                <CardTitle>Selected Cabinets</CardTitle>
                            </CardHeader>
                            <CardContent className="pt-4 px-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                                {selectedCabinets.map(cabinet => (
                                    <div key={cabinet.id} className="mb-4 p-4 bg-gray-100 rounded-lg">
                                        <h3 className="font-semibold">{cabinet.name}</h3>

                                        {cabinet.dimensions && (
                                            <div className="mt-2 space-y-1">
                                                <p>Width: {cabinet.dimensions.width}"</p>
                                                <p>Depth: {cabinet.dimensions.depth}"</p>
                                                <p>Height: {cabinet.dimensions.height}"</p>
                                                {!cabinet.sizeType && <p>Custom Product: {cabinet.productId}</p>}
                                                {cabinet.sizeType && <p>Size type: {cabinet.sizeType}</p>}
                                                {cabinet.additionalInfo && (
                                                    <p>Additional Info: {cabinet.additionalInfo}</p>
                                                )}
                                                {cabinet.productId && cabinet.sizeType && (
                                                    <p>Product #: {cabinet.productId}</p>
                                                )}
                                                {cabinet.panelReady && <p>Panel Ready: Yes</p>}
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between mt-4">
                                            <Button onClick={() => openDialog(cabinet)} variant="secondary">
                                                Edit
                                            </Button>
                                            <Button
                                                onClick={() => handleRemoveCabinet(cabinet.id)}
                                                variant="secondary"
                                                size="icon"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                        {/* <Button onClick={handleComplete}>Complete This Section</Button> */}
                    </>
                )}
                <ApplianceOptionsDialog
                    isOpen={isOptionsDialogOpen}
                    onClose={() => {
                        setIsOptionsDialogOpen(false);
                        setSelectedCabinetForOptions(null);
                    }}
                    onDelete={handleDeleteFromOptions}
                    onAddAnother={handleAddAnotherFromOptions}
                    applianceName={selectedCabinetForOptions?.name || ''}
                />
                <ApplianceDeleteDialog
                    isOpen={isDeleteDialogOpen}
                    onClose={() => {
                        setIsDeleteDialogOpen(false);
                        setCategoryToDelete(null);
                    }}
                    onDelete={cabinetId => {
                        handleRemoveCabinet(cabinetId);
                        setIsDeleteDialogOpen(false);
                        setCategoryToDelete(null);
                    }}
                    onDeleteAll={() => {
                        if (categoryToDelete) {
                            const baseId = categoryToDelete.baseId;

                            setSelectedCabinets(prev => {
                                const newSelectedCabinets = prev.filter(a => !a.id.startsWith(baseId));
                                const isCustomCabinet = !cabinetConfig.cabinets.find(a => a.id === baseId);

                                if (isCustomCabinet) {
                                    setAllCabinets(prevAll => prevAll.filter(a => a.id !== baseId));
                                }

                                return newSelectedCabinets;
                            });
                        }
                        setIsDeleteDialogOpen(false);
                        setCategoryToDelete(null);
                    }}
                    appliances={categoryToDelete?.cabinets || []}
                    categoryName={categoryToDelete?.name || ''}
                />
            </main>
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-end items-center shadow-lg">
                <Button onClick={handleComplete}>Complete This Section</Button>
            </div>
            <Dialog open={isDialogOpen && newCabinetsOther} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Enter Cabinet Type</DialogTitle>
                    </DialogHeader>
                    <div>
                        <Input
                            id="newOtherCabinets"
                            value={customName}
                            onChange={e => setCustomName(e.target.value)}
                        />
                    </div>
                    <DialogFooter>
                        <Button onClick={closeDialog} variant="secondary">
                            Cancel
                        </Button>
                        <Button onClick={handleOtherCabinetSave} disabled={!customName.trim()}>
                            Continue
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={isDialogOpen && !newCabinetsOther} onOpenChange={setIsDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{currentCabinet?.name}</DialogTitle>
                    </DialogHeader>

                    {currentCabinet && cabinetConfig.cabinets.find(cab => cab.id === currentCabinet.id)?.cabinetTypes?.length > 0 && (
                        <div className="space-y-4 mb-4">
                            <Label>Select Type</Label>
                            <select
                                value={cabinetType}
                                onChange={e => setCabinetType(e.target.value)}
                                className="w-full p-2 border rounded-md"
                                required
                            >
                                <option value="">Select {currentCabinet.name} Type</option>
                                {cabinetConfig.cabinets.find(cab => cab.id === currentCabinet.id)?.cabinetTypes.map(type => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {(!cabinetConfig.cabinets.find(cab => cab.id === currentCabinet?.id)?.cabinetTypes?.length || cabinetType) && (
                        <>
                            <RadioGroup value={sizeType} onValueChange={handleSizeTypeChange}>
                                {!otherParametrs && (
                                    <div className="flex items-center space-x-2">
                                        <RadioGroupItem value="standard" id="standard" />
                                        <Label htmlFor="standard">Standard Size</Label>
                                    </div>
                                )}
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="custom" id="custom" />
                                    <Label htmlFor="custom">Custom Size</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="product" id="product" />
                                    <Label htmlFor="product">Search Product</Label>
                                </div>
                            </RadioGroup>
                            {(sizeType === 'standard' || sizeType === 'custom') && renderPanelReadyCheckbox()}
                            {sizeType === 'standard' && !otherParametrs && (
                                <div className="space-y-4">
                                    <Label>Select Standard Size</Label>
                                    <select
                                        value={standardSize}
                                        onChange={handleChangeSize}
                                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    >
                                        {(cabinetConfig.cabinets.find(app => app.id === currentCabinet.id)?.standardSizes || []).map((size, index) => (
                                            <option key={index} value={JSON.stringify(size)}>
                                                {`W: ${size.width}" × D: ${size.depth}" × H: ${size.height}"`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {sizeType === 'custom' && (
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="width">Width (inches)</Label>
                                        <Input
                                            id="width"
                                            value={customSize.width}
                                            onChange={e => setCustomSize({ ...customSize, width: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="depth">Depth (inches)</Label>
                                        <Input
                                            id="depth"
                                            value={customSize.depth}
                                            onChange={e => setCustomSize({ ...customSize, depth: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="height">Height (inches)</Label>
                                        <Input
                                            id="height"
                                            value={customSize.height}
                                            onChange={e => setCustomSize({ ...customSize, height: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="additionalInfo">Additional Info</Label>
                                        <Input
                                            id="additionalInfo"
                                            value={customSize.additionalInfo}
                                            onChange={e =>
                                                setCustomSize({ ...customSize, additionalInfo: e.target.value })
                                            }
                                        />
                                    </div>
                                </div>
                            )}

                            {sizeType === 'product' && renderProductSearch()}
                        </>
                    )}

                    <DialogFooter>
                        <Button onClick={closeDialog} variant="secondary">
                            Cancel
                        </Button>
                        {sizeType !== 'product' && (
                            <Button
                                onClick={handleSaveCabinet}
                                disabled={
                                    (sizeType === 'custom' && !isCustomSizeValid()) ||
                                    (sizeType === 'standard' && !isStandardSizeValid()) ||
                                    ((currentCabinet?.id === 'sink' || currentCabinet?.id === 'microwave') &&
                                        !cabinetType)
                                }
                            >
                                Save
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CabinetSelector;
